import { Basket, CheckoutState } from './checkoutState';
import { config } from './config';
import { Step } from './step';

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    dataLayer: any;
  }
}

window.dataLayer = window.dataLayer || [];

const formatValue = (value: number) => value / 100 ?? 0;

export const getPriceBi = (basket: Basket, id: string) => {
  const basketItems = basket.packages.flatMap(({ items }) => items);
  const basketItem = basketItems.find(item => item.id === id);

  if (!basketItem) {
    return null;
  }

  const { costWithTaxWithDiscount, costWithTaxWithoutDiscount } = basketItem.total;
  const { sale, campaign } = basketItem.discount;

  return `${formatValue(costWithTaxWithDiscount)}|${formatValue(sale)}|${formatValue(campaign)}|${formatValue(
    costWithTaxWithoutDiscount
  )}`;
};

export const getAgeFromBirthDate = (birthDate: string) => {
  const currentYear = new Date().getFullYear();
  const birthYear = parseFloat(birthDate.split('-')[0]);
  const age = currentYear - birthYear;

  return age;
};

export const getGaEcommerceObject = (checkoutState: CheckoutState) => {
  const { currency } = config;
  const {
    orderState: {
      basket: { total, packages, vouchers },
      shipping,
      payment,
    },
  } = checkoutState;
  const selectedCarrier = shipping?.selected?.[0];
  const selectedPaymentMethod = payment?.selected?.[0];
  const basketItems = packages.flatMap(({ items }) => items);

  return {
    currency,
    value: formatValue(total.costWithTaxWithVouchersWithServiceCosts),
    payment_type: selectedPaymentMethod?.paymentOptionKey,
    shipping_tier: selectedCarrier?.carrierKey,
    coupon: vouchers ?? '',
    items: basketItems.map((item, index) => {
      const mainCategory = item.categories?.pop();
      return {
        item_id: item.id,
        item_name: item.name,
        price: formatValue(item.unit.costWithoutTaxWithoutVoucher),
        item_brand: item.brand.name,
        item_brand_id: item.brand.id,
        item_category: mainCategory?.name,
        item_category_id: mainCategory?.id,
        item_variant: item.variantId,
        sale: !!item.discount.sale,
        index,
        quantity: item.quantity,
      };
    }),
  };
};

export const pushTrackingEvent = (dataLayerProperties: Record<string, unknown>) => {
  window.dataLayer.push(dataLayerProperties);
  window.parent.postMessage(
    {
      type: 'tracking',
      event: dataLayerProperties,
    },
    '*'
  );
};

export const pushCheckoutStateTrackingEvents = (initialCheckoutState: CheckoutState, step: Step) => {
  pushTrackingEvent({
    event: 'shop_init',
    shop_id: config.appId,
    shop_version: '<git hash>',
    shop_gender: initialCheckoutState?.customer?.data.gender,
    locale: config.locale,
    shop_currency: config.currency,
    landing_page: window.location.href,
    parameter: window.location.search,
    referrer: document?.referrer,
    deeplink: '<deeplink>',
    checkout_version: 'v3',
    origin: config.origin,
    ecommerce: getGaEcommerceObject(initialCheckoutState),
  });

  pushTrackingEvent({
    event: 'content_view',
    content_name: `${window.location.pathname}/${step}`,
    search: window.location.search || null,
    app_version: 'v3',
    appId: config.appId,
    page_type: 'checkout:' + step,
  });

  pushTrackingEvent({
    event: 'customer_data',
    customer_id: initialCheckoutState.customer?.data.id,
    customer_type: initialCheckoutState?.customer
      ? initialCheckoutState.customer.flags?.isGuest
        ? 'guest'
        : 'existing'
      : 'new',
    age: initialCheckoutState.customer?.data?.birthDate
      ? getAgeFromBirthDate(initialCheckoutState.customer.data.birthDate)
      : null,
    gender: initialCheckoutState.customer?.data.gender,
    sessions_id_co: 'sessionIdCheckout',
    eh: initialCheckoutState.customer?.data.trackingHash,
    data: {
      customerAddedNumber: !!initialCheckoutState.customer?.data.phone,
    },
    logged_in: 'email',
  });
};

export const pushErrorTrackingEvent = (action: string, notificationType: string, notificationText: string) => {
  pushTrackingEvent({
    event: 'error',
    action,
    label: `${config.locale}|${notificationType}|${notificationText}`,
    value: null,
  });
};
